/**
 * useOfflineMode - Hook principal pour la gestion du mode hors-ligne
 *
 * Combine la détection online/offline, la synchronisation du catalogue
 * et la gestion de la queue de ventes.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  offlineDb,
  CatalogSync,
  SalesQueue,
  PendingSale,
  SyncProgress,
} from '../lib/offline';
import { useTenant } from '../contexts/TenantContext';
import { logger } from '../utils/logger';

// ============================================================================
// TYPES
// ============================================================================

export interface OfflineModeState {
  // État de connexion
  isOnline: boolean;

  // Synchronisation du catalogue
  isSyncingCatalog: boolean;
  catalogSyncProgress: SyncProgress | null;
  lastCatalogSync: Date | null;
  catalogNeedsSync: boolean;

  // Queue de ventes
  isSyncingSales: boolean;
  pendingSalesCount: number;
  conflictSalesCount: number;
  errorSalesCount: number;

  // Stats
  offlineProductsCount: number;

  // Erreurs
  error: string | null;
}

export interface UseOfflineModeReturn extends OfflineModeState {
  // Actions catalogue
  syncCatalog: (force?: boolean) => Promise<void>;
  abortCatalogSync: () => void;

  // Actions ventes
  syncSales: () => Promise<void>;
  getConflictSales: () => Promise<PendingSale[]>;
  getErrorSales: () => Promise<PendingSale[]>;
  forceFullSale: (saleId: string) => Promise<boolean>;
  retrySale: (saleId: string) => Promise<boolean>;
  abandonSale: (saleId: string, reason: string) => Promise<boolean>;

  // Accès aux services
  salesQueue: SalesQueue | null;
  catalogSync: CatalogSync | null;
}

// ============================================================================
// HOOK PRINCIPAL
// ============================================================================

export function useOfflineMode(): UseOfflineModeReturn {
  const { tenant } = useTenant();
  const tenantId = tenant?.id;

  // Refs pour les services
  const salesQueueRef = useRef<SalesQueue | null>(null);
  const catalogSyncRef = useRef<CatalogSync | null>(null);

  // État local
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSyncingCatalog, setIsSyncingCatalog] = useState(false);
  const [isSyncingSales, setIsSyncingSales] = useState(false);
  const [catalogSyncProgress, setCatalogSyncProgress] = useState<SyncProgress | null>(null);
  const [lastCatalogSync, setLastCatalogSync] = useState<Date | null>(null);
  const [catalogNeedsSync, setCatalogNeedsSync] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ============================================================================
  // QUERIES RÉACTIVES (Dexie Live Query)
  // ============================================================================

  // Nombre de ventes non confirmées au serveur (réactif). On compte 'pending' ET 'syncing' :
  // une vente en cours d'envoi (ou figée) ne doit PAS être invisible (sinon elle pourrait se
  // perdre en silence). Ça garde aussi la sync périodique active tant qu'il reste du 'syncing'.
  const pendingSalesCount = useLiveQuery(
    () => tenantId
      ? offlineDb.pendingSales.where('tenant_id').equals(tenantId)
          .and(s => s.status === 'pending' || s.status === 'syncing').count()
      : 0,
    [tenantId],
    0
  );

  // Nombre de ventes en conflit (réactif)
  const conflictSalesCount = useLiveQuery(
    () => tenantId
      ? offlineDb.pendingSales.where('tenant_id').equals(tenantId).and(s => s.status === 'conflict').count()
      : 0,
    [tenantId],
    0
  );

  // Nombre de ventes en erreur (réactif)
  const errorSalesCount = useLiveQuery(
    () => tenantId
      ? offlineDb.pendingSales.where('tenant_id').equals(tenantId).and(s => s.status === 'error' || (s.status === 'abandoned' && !s.abandon_synced)).count()
      : 0,
    [tenantId],
    0
  );

  // Nombre de produits offline (réactif)
  const offlineProductsCount = useLiveQuery(
    () => tenantId
      ? offlineDb.products.where('tenant_id').equals(tenantId).count()
      : 0,
    [tenantId],
    0
  );

  // ============================================================================
  // INITIALISATION DES SERVICES
  // ============================================================================

  useEffect(() => {
    if (tenantId) {
      // Créer les services
      salesQueueRef.current = new SalesQueue(tenantId);
      catalogSyncRef.current = new CatalogSync(tenantId, setCatalogSyncProgress);

      // RECOVERY AU DÉMARRAGE : récupérer les ventes bloquées en 'syncing' d'une session
      // précédente crashée/fermée. Crucial car une vente 'syncing' n'est PAS comptée dans
      // pendingSalesCount → la sync périodique (qui exige pendingSalesCount > 0) ne se
      // déclencherait jamais et la vente encaissée serait perdue à jamais.
      // La garde est `n > 0` SEULEMENT : conditionner cette reprise au drapeau du
      // navigateur, c'était laisser une vente encaissée bloquée en 'syncing'
      // (donc invisible à `pendingSalesCount`) sur une caisse dont le drapeau
      // est figé. La file décide elle-même si la requête vaut la peine.
      salesQueueRef.current.recoverStaleSyncing().then((n) => {
        if (n > 0) {
          salesQueueRef.current?.syncPendingSales().catch(() => {});
        }
      });

      // Vérifier si une sync est nécessaire — ET la lancer tout de suite.
      //
      // Elle ne partait qu'à la connexion du premier VENDEUR, c'est-à-dire à
      // l'ouverture du magasin : la synchro complète (forcée toutes les 24 h,
      // 1 603 produits, 2,9 Mo) tombait pile sur les premiers clients, et
      // chaque scan lisait la base locale pendant qu'elle était réécrite.
      // Le Mac mini, lui, est allumé bien avant : autant faire ce travail
      // pendant que le magasin est vide. Le garde de 3 minutes de la connexion
      // vendeur évite ensuite le doublon.
      catalogSyncRef.current.needsSync().then((besoin) => {
        setCatalogNeedsSync(besoin);
        if (!besoin || !catalogSyncRef.current) return;
        setIsSyncingCatalog(true);
        catalogSyncRef.current
          .syncAll()
          .then((result) => {
            setLastCatalogSync(new Date());
            setCatalogNeedsSync(false);
            logger.log('[useOfflineMode] Catalogue préchargé au démarrage:', result.products?.synced);
          })
          // Silencieux : la caisse doit ouvrir même sans réseau, et la sync
          // repartira à la connexion du vendeur.
          .catch((err) => logger.warn('[useOfflineMode] Préchargement catalogue échoué:', err))
          .finally(() => setIsSyncingCatalog(false));
      });
      catalogSyncRef.current.getLastSyncDate().then(setLastCatalogSync);

      logger.log('[useOfflineMode] Services initialisés pour tenant:', tenantId);
    }

    return () => {
      // Cleanup
      catalogSyncRef.current?.abort();
    };
  }, [tenantId]);

  // ============================================================================
  // DÉTECTION ONLINE/OFFLINE (avec vérification réelle)
  // ============================================================================

  /**
   * Vérifier la connectivité réelle via un ping API.
   *
   * `navigator.onLine` n'est fiable dans AUCUN des deux sens :
   * - il vaut parfois `true` sans connexion serveur (lien 4G tombé derrière le
   *   modem) — c'est le cas que traite la re-vérification périodique ci-dessous ;
   * - il reste parfois bloqué à `false` alors que le réseau est revenu
   *   (Safari/macOS après un réveil Wi-Fi ou un changement de réseau).
   *
   * Ce second cas était un piège fermé sur lui-même : un `return false` immédiat
   * empêchait la seule requête capable de prouver le retour du réseau, et la
   * caisse restait « hors ligne » indéfiniment — y compris à travers la relance
   * de 20 s, qui appelle justement cette fonction (POS-03, 06/09/2026).
   *
   * On interroge donc TOUJOURS le serveur : sa réponse est la seule preuve qui
   * vaille. Le coût est une requête HEAD, plafonnée à 3 secondes.
   */
  const checkRealConnectivity = useCallback(async (): Promise<boolean> => {
    let joignable = false;
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000);
      const response = await fetch('/api/health', {
        method: 'HEAD',
        cache: 'no-store',
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      joignable = response.ok;
    } catch {
      joignable = false;
    }
    // La file de ventes n'a pas d'autre moyen de savoir : c'est ici, et nulle
    // part ailleurs, que l'état du réseau est PROUVÉ. Sans ce report, elle
    // continuerait de consulter le drapeau du navigateur — celui qui reste
    // bloqué à « faux ». Voir SalesQueue.setServerReachable.
    salesQueueRef.current?.setServerReachable(joignable);

    // Une sonde qui ÉCHOUE remet l'écran en « hors ligne », d'où qu'elle vienne.
    //
    // C'est structurel, pas cosmétique : la re-vérification périodique ne tourne
    // que tant qu'on se croit hors ligne (`if (isOnline) return`). Si une sonde
    // échouait en laissant le badge au vert, la file serait déclarée injoignable
    // ET plus rien ne viendrait la détromper — le drapeau bloqué renaîtrait sous
    // un autre nom. Le cas s'est présenté ici même : l'événement `online` du
    // navigateur relance la sonde, et un seul échec passager à cet instant
    // (3 s de plafond, 4G du marché, Wi-Fi qui se réassocie) refermait le piège
    // pour la journée, sans qu'aucun `online` ultérieur ne reparte.
    //
    // Le succès, lui, n'est PAS traité ici : repasser en ligne suppose aussi de
    // rattraper la file, et c'est le travail de `passerEnLigne`.
    if (!joignable) setIsOnline(false);
    return joignable;
  }, []);

  /**
   * Le serveur répond de nouveau : on repasse en ligne et on rattrape.
   * Extrait pour être appelé par l'événement du navigateur ET par la
   * re-vérification périodique ci-dessous.
   */
  const passerEnLigne = useCallback(async () => {
    logger.log('[useOfflineMode] 🟢 Connexion serveur confirmée');
    setIsOnline(true);
    setError(null);

    // Synchroniser automatiquement les ventes en attente
    if (salesQueueRef.current) {
      // Ceinture : on n'arrive ici qu'après une sonde réussie, mais la file
      // refuserait de partir si sa dernière preuve datait d'un échec.
      salesQueueRef.current.setServerReachable(true);
      try {
        setIsSyncingSales(true);
        await salesQueueRef.current.syncPendingSales();
      } catch (err) {
        logger.error('[useOfflineMode] Erreur sync ventes:', err);
      } finally {
        setIsSyncingSales(false);
      }
    }

    // Vérifier si le catalogue a besoin d'être synchronisé
    if (catalogSyncRef.current) {
      const needsSync = await catalogSyncRef.current.needsSync();
      setCatalogNeedsSync(needsSync);
    }
  }, []);

  useEffect(() => {
    const handleOnline = async () => {
      logger.log('[useOfflineMode] 🟡 navigator.onLine = true, vérification...');

      // Vérifier la connectivité réelle
      const reallyOnline = await checkRealConnectivity();
      if (!reallyOnline) {
        // `checkRealConnectivity` a déjà remis l'écran hors ligne : la
        // re-vérification de 20 s reprend donc la main d'elle-même.
        logger.log('[useOfflineMode] 🟠 Pas de connexion serveur, on reste hors ligne');
        return;
      }

      await passerEnLigne();
    };

    const handleOffline = () => {
      logger.log('[useOfflineMode] 🔴 Application offline');
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Vérification initiale au montage
    checkRealConnectivity().then(online => {
      if (online !== isOnline) {
        setIsOnline(online);
        logger.log(`[useOfflineMode] État initial: ${online ? '🟢 online' : '🔴 offline'}`);
      }
    });

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [checkRealConnectivity, passerEnLigne]);

  // ============================================================================
  // RETOUR DU RÉSEAU — re-vérification périodique
  // ============================================================================
  //
  // `isOnline` ne changeait QUE sur les événements `online` / `offline` du
  // navigateur. Or au marché, le cas courant n'est pas la perte du Wi-Fi mais
  // la perte du lien 4G DERRIÈRE le modem : le Mac reste associé, aucun
  // événement ne part, et la caisse restait « hors ligne » pour le reste de la
  // journée — file de ventes bloquée, clôture Z refusée. Même chose quand
  // l'événement `online` arrive avant que le serveur ne réponde (redémarrage du
  // routeur, ré-attachement 4G : 10 à 60 s) : la vérification échouait une fois
  // et plus rien ne la relançait.
  //
  // On redemande donc toutes les 20 secondes, tant qu'on se croit hors ligne.
  useEffect(() => {
    if (isOnline) return;
    const interval = setInterval(async () => {
      const revenu = await checkRealConnectivity();
      if (revenu) {
        logger.log('[useOfflineMode] 🔁 Le serveur répond de nouveau');
        await passerEnLigne();
      }
    }, 20000);
    return () => clearInterval(interval);
  }, [isOnline, checkRealConnectivity, passerEnLigne]);

  // ============================================================================
  // SYNC PÉRIODIQUE DES VENTES (quand online)
  // ============================================================================

  useEffect(() => {
    if (!isOnline || !salesQueueRef.current) return;

    // Sync toutes les 30 secondes si des ventes sont en attente
    const interval = setInterval(async () => {
      // Plus de `navigator.onLine` ici : cet effet ne tourne déjà que lorsque
      // `isOnline` est vrai, et `isOnline` est PROUVÉ par la sonde serveur.
      // Ajouter le drapeau par-dessus, c'était rendre ce filet muet exactement
      // quand il sert — badge vert, file qui ne se vide pas.
      // Conflits et erreurs aussi : sans eux, une vente en conflit seule n'était
      // jamais relancée (la sync ne partait que s'il restait une vente en attente).
      if (salesQueueRef.current && (pendingSalesCount > 0 || conflictSalesCount > 0 || errorSalesCount > 0)) {
        try {
          await salesQueueRef.current.syncPendingSales();
        } catch (err) {
          logger.error('[useOfflineMode] Erreur sync périodique:', err);
        }
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [isOnline, pendingSalesCount, conflictSalesCount, errorSalesCount]);

  // ============================================================================
  // NOTIFICATIONS POUR CONFLITS/ERREURS
  // ============================================================================

  const prevConflictCountRef = useRef(conflictSalesCount);
  const prevErrorCountRef = useRef(errorSalesCount);

  useEffect(() => {
    // Détecter nouveaux conflits
    if (conflictSalesCount > prevConflictCountRef.current) {
      const newConflicts = conflictSalesCount - prevConflictCountRef.current;
      logger.warn(`[useOfflineMode] ⚠️ ${newConflicts} nouveau(x) conflit(s) de stock détecté(s)`);

      // Notification visuelle (si API disponible)
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification('Conflit de stock', {
          body: `${newConflicts} vente(s) en conflit. Vérifiez l'indicateur offline.`,
          icon: '/pwa-192x192.png',
          tag: 'stock-conflict',
        });
      }

      // Son d'alerte (optionnel - peut être activé/désactivé via config)
      try {
        const audio = new Audio('/sounds/alert.mp3');
        audio.volume = 0.3;
        audio.play().catch(() => {
          // Ignorer si le son ne peut pas être joué (autoplay policy)
        });
      } catch {
        // Ignorer si le fichier audio n'existe pas
      }
    }

    // Détecter nouvelles erreurs
    if (errorSalesCount > prevErrorCountRef.current) {
      const newErrors = errorSalesCount - prevErrorCountRef.current;
      logger.error(`[useOfflineMode] ❌ ${newErrors} nouvelle(s) erreur(s) de sync`);
    }

    prevConflictCountRef.current = conflictSalesCount;
    prevErrorCountRef.current = errorSalesCount;
  }, [conflictSalesCount, errorSalesCount]);

  // ============================================================================
  // ACTIONS
  // ============================================================================

  /**
   * Synchroniser le catalogue
   */
  const syncCatalog = useCallback(async (force = false) => {
    if (!catalogSyncRef.current) return;

    // Vérifier si nécessaire (sauf si force)
    if (!force) {
      const needsSync = await catalogSyncRef.current.needsSync();
      if (!needsSync) {
        logger.log('[useOfflineMode] Catalogue à jour, sync non nécessaire');
        return;
      }
    }

    setIsSyncingCatalog(true);
    setError(null);

    try {
      const result = await catalogSyncRef.current.syncAll();

      if (result.success) {
        setLastCatalogSync(new Date());
        setCatalogNeedsSync(false);
        logger.log('[useOfflineMode] ✅ Catalogue synchronisé:', result);
      } else {
        setError(result.error || 'Erreur de synchronisation');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur inconnue';
      setError(message);
      logger.error('[useOfflineMode] ❌ Erreur sync catalogue:', err);
    } finally {
      setIsSyncingCatalog(false);
      setCatalogSyncProgress(null);
    }
  }, []);

  /**
   * Annuler la synchronisation du catalogue
   */
  const abortCatalogSync = useCallback(() => {
    catalogSyncRef.current?.abort();
    setIsSyncingCatalog(false);
    setCatalogSyncProgress(null);
  }, []);

  /**
   * Synchroniser les ventes
   */
  const syncSales = useCallback(async () => {
    if (!salesQueueRef.current || !isOnline) return;

    setIsSyncingSales(true);
    try {
      await salesQueueRef.current.syncPendingSales();
    } catch (err) {
      logger.error('[useOfflineMode] Erreur sync ventes:', err);
    } finally {
      setIsSyncingSales(false);
    }
  }, [isOnline]);

  /**
   * Obtenir les ventes en conflit
   */
  const getConflictSales = useCallback(async (): Promise<PendingSale[]> => {
    if (!salesQueueRef.current) return [];
    return salesQueueRef.current.getConflictSales();
  }, []);

  /**
   * Obtenir les ventes en erreur
   */
  const getErrorSales = useCallback(async (): Promise<PendingSale[]> => {
    if (!salesQueueRef.current) return [];
    return salesQueueRef.current.getErrorSales();
  }, []);

  /**
   * Forcer l'enregistrement de la vente complète malgré un stock insuffisant
   */
  const forceFullSale = useCallback(async (saleId: string): Promise<boolean> => {
    if (!salesQueueRef.current) return false;
    return salesQueueRef.current.forceFullSale(saleId);
  }, []);

  /**
   * Réessayer une vente en erreur
   */
  const retrySale = useCallback(async (saleId: string): Promise<boolean> => {
    if (!salesQueueRef.current) return false;
    return salesQueueRef.current.retrySale(saleId);
  }, []);

  /**
   * Annuler une vente non synchronisée
   */
  const abandonSale = useCallback(async (saleId: string, reason: string): Promise<boolean> => {
    if (!salesQueueRef.current) return false;
    return salesQueueRef.current.abandonPendingSale(saleId, reason);
  }, []);

  // ============================================================================
  // RETURN
  // ============================================================================

  return {
    // État
    isOnline,
    isSyncingCatalog,
    catalogSyncProgress,
    lastCatalogSync,
    catalogNeedsSync,
    isSyncingSales,
    pendingSalesCount: pendingSalesCount ?? 0,
    conflictSalesCount: conflictSalesCount ?? 0,
    errorSalesCount: errorSalesCount ?? 0,
    offlineProductsCount: offlineProductsCount ?? 0,
    error,

    // Actions catalogue
    syncCatalog,
    abortCatalogSync,

    // Actions ventes
    syncSales,
    getConflictSales,
    getErrorSales,
    forceFullSale,
    retrySale,
    abandonSale,

    // Services
    salesQueue: salesQueueRef.current,
    catalogSync: catalogSyncRef.current,
  };
}
